/** Три экрана для того, кто открыл приложение впервые.
 *
 * ПОЧЕМУ ПО ДАННЫМ, А НЕ ПО ФЛАГУ. «Первый раз» определяется тем, что лимит не
 * задан, а не отметкой в хранилище. Хранилище чистится, приложение открывают с
 * другого телефона — и человек, который всё настроил, снова получает обучение.
 * Отметка нужна только тому, кто нажал «Потом»: он уже всё видел и лимит задавать
 * не собирается прямо сейчас.
 *
 * Три шага, а не один: каждый отвечает на свой вопрос — как записывать, зачем
 * лимит, что с валютами. Свалить их в один абзац значит, что не прочтут ни один.
 */
import { useState } from "react";
import { tap } from "../lib/telegram.ts";

const STEPS = [
  {
    title: "Говори как говоришь",
    body: "«Кофе 60к, такси 5 баксов, обед 180 тысяч» — одной фразой, хоть голосом. "
      + "Разберёт сам: сумму, валюту и категорию.",
    art: [
      "M4 5.5A1.5 1.5 0 015.5 4h13A1.5 1.5 0 0120 5.5v9a1.5 1.5 0 01-1.5 1.5H9l-5 4z",
      "M8 8.5h8M8 11.8h5",
    ],
  },
  {
    title: "Скажи, сколько готов тратить",
    body: "На каждой трате будет видно, сколько ещё можно до конца месяца. "
      + "Это не запрет и не осуждение — просто ответ на вопрос, который и так задаёшь.",
    art: [
      "M12 3.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17z",
      "M12 7.5v5l3.2 2",
    ],
  },
  {
    title: "Плати в любой валюте",
    body: "Донги, доллары, рубли — считать итоги будем в одной, по курсу того дня, "
      + "когда потратил. Задним числом ничего не пересчитывается.",
    art: [
      "M3.5 8.5h13M13 5l3.5 3.5L13 12",
      "M20.5 15.5h-13M11 12l-3.5 3.5L11 19",
    ],
  },
];

export function Onboarding({ onSetLimit, onSkip }: {
  onSetLimit: () => void;
  onSkip: () => void;
}) {
  const [step, setStep] = useState(0);
  const last = step === STEPS.length - 1;
  const current = STEPS[step]!;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100dvh",
                  // Знакомство идёт мимо --screen: у него свой ритм на весь
                  // экран. Верхний хром Telegram от этого никуда не девается.
                  padding: `calc(32px + var(--top-chrome)) 24px calc(24px + var(--tg-safe-bottom, 0px))` }}>
      <div style={{ display: "flex", gap: 6, marginBottom: "var(--s-8)" }}
           role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length}
           aria-valuenow={step + 1} aria-label="Шаг знакомства">
        {STEPS.map((s, i) => (
          <span key={s.title} style={{ flexGrow: 1, height: 3, borderRadius: 2,
                  background: i <= step ? "var(--accent)" : "var(--surface-3)" }} />
        ))}
      </div>

      <div style={{ flexGrow: 1, display: "flex", flexDirection: "column",
                    justifyContent: "center", gap: "var(--s-5)" }}>
        <span aria-hidden="true"
              style={{ width: 64, height: 64, borderRadius: "50%",
                       background: "var(--accent-tint)", display: "flex",
                       alignItems: "center", justifyContent: "center" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
               strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            {current.art.map((d) => <path key={d} d={d} />)}
          </svg>
        </span>
        {/* Заголовок один на экран и меняется вместе с шагом: читалка должна
            объявлять новый текст, а не молчать, пока картинка сменилась. */}
        <h1 key={current.title} style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                     letterSpacing: "-0.8px", lineHeight: 1.15, margin: 0 }}>
          {current.title}
        </h1>
        <p style={{ fontSize: "var(--f-body)", lineHeight: 1.6, margin: 0,
                    color: "var(--text-muted)" }}>
          {current.body}
        </p>
      </div>

      <button type="button" onClick={() => { tap(); last ? onSetLimit() : setStep(step + 1); }}
              style={{ width: "100%", minHeight: "var(--tap)", padding: 15,
                       borderRadius: "var(--r-field)", border: "none", cursor: "pointer",
                       font: "inherit", fontSize: "var(--f-body)", fontWeight: 600,
                       background: "var(--accent)", color: "var(--on-accent)" }}>
        {last ? "Задать лимит" : "Дальше"}
      </button>
      <button type="button" onClick={() => { tap(); onSkip(); }}
              style={{ width: "100%", minHeight: 44, marginTop: "var(--s-2)",
                       background: "none", border: "none", cursor: "pointer",
                       font: "inherit", fontSize: "var(--f-caption)",
                       color: "var(--text-muted)" }}>
        {last ? "Потом" : "Пропустить"}
      </button>
    </div>
  );
}

/** Отметка «уже видел» нужна только тому, кто нажал «Потом»: у остальных признак
 *  первого раза — отсутствующий лимит, и он честнее любого флага. */
const SEEN_KEY = "onboarding-skipped";

export function onboardingSkipped(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;   // хранилище недоступно — покажем; это не поломка
  }
}

export function skipOnboarding(): void {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Не сохранилось — увидит ещё раз. Терять нечего.
  }
}
